import { Component, ComponentFactoryResolver, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Models } from '@ibexfusion/models';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { ScreenSelectorComponent } from '../../screens/screen-selector/screen-selector.component';
import { ScreenSettingsService } from '@services/screen-settings/screen-settings.service';
import { ScreenService } from '@services/screens/screen.service';
import { FormDirective } from '../../../directives/form/form.directive';
import { PageAction } from '../../../components/page-actions/page-actions.component';
import { DefaultFormComponent } from '../../../components/default-form/default-form.component';
import { FormComponent } from '../../../components/default-form/form.component';
import { AngieScreensService } from '@services/angie-screens/angie-screens.service';

@Component({
    selector: 'app-screen-settings-edit',
    templateUrl: './screen-settings-edit.component.html',
    styleUrls: ['./screen-settings-edit.component.scss'],
    providers: [ScreenSettingsService, ScreenService, AngieScreensService],
    standalone: false
})
export class ScreenSettingsEditComponent implements OnInit {

  @ViewChild(FormDirective, { static: true }) formHost: FormDirective;

  pageActions: PageAction[];
  title = 'Create screen settings';
  isLoading = true;
  boundScreenSettings: Models.LegacyScreenSetting;
  screenUuid: string;
  screenId: string;

  screen: any;
  screenPath = 'screens';

  isNew = false;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private screenSettingService: ScreenSettingsService,
    private screenService: ScreenService,
    private angieScreensService: AngieScreensService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private snackbar: SnackbarService,
    private dialog: MatDialog
  ) {
  }

  ngOnInit() {
    this.createPageActions();
    if (this.activatedRoute.snapshot.params['id']) {
      this.screenUuid = this.activatedRoute.snapshot.params['id'];
      this.screenSettingService.find(0, 1, { screenUuid: this.screenUuid }).then(result => {
        if (!result || !result.data || result.data.length < 1) {
          this.boundScreenSettings = new Models.LegacyScreenSetting();
          this.boundScreenSettings.populateFromJSON({
            screenUuid: this.screenUuid
          });
          this.isNew = true;
        } else {
          this.boundScreenSettings = result.data[0];
        }
        this.isLoading = false;
        this.loadForm();
      });
      this.fetchScreen(this.screenUuid).then(result => {
        if (this.screen) {
          this.pageActions.push(
            new PageAction({
              icon: 'content_copy',
              title: 'Clone Settings',
              callback: () => {
                this.cloneScreenSettings();
              }
            }));
          this.screenId = this.screen._id;
          this.title = 'Edit settings for Screen -> ' + this.screen.name;
        }
      });
    } else {
      this.boundScreenSettings = <Models.LegacyScreenSetting>Models.ModelFactory.createModelInstanceByName('ScreenSetting');
      this.isLoading = false;
      this.loadForm();
    }
  }

  fetchScreen(uuid) {
    return this.screenService.find(0, 1, { uuid: uuid }).then(screen => {
      if (!screen || !screen.data || !screen.data.length) {
        return this.angieScreensService.find(0, 1, { uuid: uuid }).then(angieScreen => {
          this.screenPath = 'angie-screens';
          this.screen = angieScreen.data && angieScreen.data.length ? angieScreen.data[0] : null;
        });
      }
      this.screen = screen.data[0];
    });
  }

  loadForm() {
    const formItem = DefaultFormComponent;

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(formItem);

    const viewContainerRef = this.formHost.viewContainerRef;
    viewContainerRef.clear();

    const componentRef = viewContainerRef.createComponent(componentFactory);
    (<FormComponent>componentRef.instance).entry = this.boundScreenSettings;
  }

  onSave() {
    return this.boundScreenSettings.validate(true).then(result => {
      if (result.success) {
        if (this.isNew) {
          this.screenSettingService.create(this.boundScreenSettings).then(screenSettingResult => {
            this.snackbar.success('Form successfully saved');
          });
        } else {
          this.screenSettingService.update(this.boundScreenSettings._id, this.boundScreenSettings).then(screenSettingResult => {
            this.snackbar.success('Form successfully saved');
          });
        }
      } else {
        this.snackbar.open('Could not create the screenSetting, please fill the form correctly');
      }
    });
  }

  cloneScreenSettings() {
    this.isLoading = true;
    const dialogRef = this.dialog.open(ScreenSelectorComponent, {
      width: '600px',
    });
    dialogRef.componentInstance.customerId = this.screen.customerId;

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        this.isLoading = false;
        return;
      }
      this.screenSettingService.find(0, 1, { screenUuid: result.screenUuid }).then(setting => {
        if (setting && setting.data && setting.data.length === 1) {
          this.boundScreenSettings.populateFromJSON({ detail: setting.data[0].detail });
          this.loadForm();
          this.isLoading = false;
        }
        this.isLoading = false;
      });
    });
  }

  public createPageActions() {
    this.pageActions = [
      new PageAction({
        icon: 'desktop_windows',
        title: 'Overview Screen',
        callback: () => {
          this.router.navigate([this.screenPath, this.screenId]);
        }
      }),
      new PageAction({
        icon: 'settings',
        title: 'Overview Screen Settings',
        callback: () => {
          this.router.navigate(['screen-settings', this.activatedRoute.snapshot.params['id']]);
        }
      }),
      new PageAction({
        icon: 'save',
        title: 'Save',
        callback: () => {
          this.onSave();
        }
      }),
    ];
  }

}
